import axios from 'axios'
import type { AnalysisResult, ChatOp, ExportResult, MusicSong, Segment } from '../types'
import { IS_DEMO } from '../demo'

export const apiBaseURL = import.meta.env.VITE_API_BASE_URL || '/api'

export const buildApiUrl = (path: string) => {
  const base = apiBaseURL.endsWith('/') ? apiBaseURL.slice(0, -1) : apiBaseURL
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${base}${normalizedPath}`
}

// Create axios instance with base configuration
export const api = axios.create({
  baseURL: apiBaseURL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for adding auth headers
api.interceptors.request.use(
  (config) => {
    // Add authentication token if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    config.withCredentials = true
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // The public demo has no session by design: a 401 there is expected, and sending a visitor
    // to a login page mid-demo would end it. The UI shows its own sign-up prompt instead.
    if (error.response?.status === 401 && IS_DEMO) {
      return Promise.reject(error)
    }
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('auth_token')
      
      // If we're inside an iframe, notify the parent page to re-authenticate
      // instead of navigating the iframe to /login (which breaks Studio)
      if (window.parent !== window) {
        try {
          window.parent.postMessage({ type: 'studio-auth-expired' }, '*')
        } catch (_e) {
          // Silently ignore if parent can't receive messages
        }
        // Don't redirect — let the API call fail with 401 error
        // and show an error in the UI instead
      } else {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Studio API functions
export const studioApi = {
  // Import video metadata
  async importMetadata(url: string) {
    const response = await api.post('/studio/import/metadata', { url })
    return response.data
  },

  // Analyze video for segments
  async analyzeVideo(videoId: string, options: {
    url?: string
    detection_mode?: 'llm' | 'visual' | 'enhanced'
    max_segments?: number
    min_segment_duration?: number
    max_segment_duration?: number
  } = {}) {
    const response = await api.post('/studio/analyze', { video_id: videoId, ...options }, { timeout: 300000 })
    return response.data as AnalysisResult
  },

  // Get video segments
  async getSegments(videoId: string) {
    const response = await api.get(`/studio/segments/${videoId}`)
    return response.data
  },

  // Update segment
  async updateSegment(segmentId: string, updates: Partial<Segment>) {
    const response = await api.put(`/studio/segments/${segmentId}`, updates)
    return response.data
  },

  // Delete segment
  async deleteSegment(segmentId: string) {
    const response = await api.delete(`/studio/segments/${segmentId}`)
    return response.data
  },

  // Export clips
  async exportClips(request: {
    video_id: string
    segment_ids: string[]
    format?: 'mp4' | 'avi' | 'mov'
    quality?: '720p' | '1080p' | '4k'
    aspect_ratio?: '16:9' | '9:16' | '1:1'
    include_captions?: boolean
  }) {
    const response = await api.post('/studio/export', request)
    return response.data as ExportResult
  },

  // Get export status
  async getExportStatus(exportId: string) {
    const response = await api.get(`/studio/export/${exportId}`)
    return response.data
  },

  // Download exported clip
  async downloadClip(url: string, filename: string) {
    const response = await api.get(url, {
      responseType: 'blob',
    })
    
    // Create download link
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)
  },

  // Chat with video transcript (v2: sentence-indexed ops over the current timeline)
  async chatWithVideo(request: {
    session_id?: string
    message: string
    history?: Array<{ role: string; content: string }>
    transcript?: any
    // Chat v2 fields — sentence-level editing context
    sentences?: Array<{ start: number; end: number; text: string }>
    segments?: Array<{ id: string; start: number; end: number; title: string }>
    // What audio is ALREADY on the timeline. Required for remove_sound: the
    // assistant can only remove sounds it has ids for.
    sounds?: Array<{ id: string; kind: string; name: string; start: number; duration: number }>
    video_duration?: number
    settings?: {
      max_clips?: number
      min_duration?: number
      max_duration?: number
    }
  }) {
    const response = await api.post('/studio/chat', request, { timeout: 120000 })
    return response.data as {
      success: boolean
      message: string
      // Chat v2: assistant reply + resolved timeline ops (timestamps already mapped)
      reply?: string
      ops?: ChatOp[]
      highlights: Array<{
        start_time: number
        end_time: number
        title: string
        description: string
        importance_score: number
        tags: string[]
      }>
      segments: Array<{
        id: string
        video_id: string
        start: number
        end: number
        text: string
        highlight_score: number
        color: string
      }>
      highlight_count: number
    }
  },
}

// ── Music Library API (Synestia proxy) ──────────────────────────
export const musicApi = {
  /** Search the Synestia music library (proxied through frontend-web) */
  async searchSongs(params: { q?: string; genre?: string; limit?: number } = {}) {
    const response = await api.get('/studio/music/search', {
      params: {
        q: params.q || '',
        genre: params.genre || '',
        limit: params.limit || 25,
      },
    })
    return response.data as { songs: MusicSong[] }
  },

  /** Genres (categories) with track counts */
  async getGenres() {
    const response = await api.get('/studio/music/genres')
    return response.data as { genres: { genre: string; count: number }[] }
  },

  /** Same-origin stream URL for <audio> playback (Range-enabled proxy) */
  getStreamUrl(filename: string) {
    return buildApiUrl(`/studio/music/stream/${encodeURIComponent(filename)}`)
  },

  /** Kick off an original-music generation job (legit2compute) */
  async generate(params: { prompt: string; duration: number }) {
    const response = await api.post('/studio/music/generate', params)
    return response.data as { success?: boolean; job_id: string }
  },

  /** Poll a music-generation job status */
  async getGenStatus(jobId: string) {
    const response = await api.get(`/studio/music/gen-status/${jobId}`)
    return response.data as {
      status: string
      progress?: number
      error_message?: string
      ready: boolean
      download_url?: string
    }
  },

  /** Same-origin download/stream URL for a finished generated track */
  getGenDownloadUrl(jobId: string) {
    return buildApiUrl(`/studio/music/gen-download/${jobId}`)
  },
}

// ── SFX Generation API (MOSS via legit2compute) ─────────────────
export const sfxApi = {
  /** Browse the curated SFX library (100 top-production SFX) */
  async library(params?: { category?: string; q?: string }) {
    const response = await api.get('/studio/sfx/library', { params })
    return response.data as {
      success?: boolean
      count?: number
      categories: string[]
      sfx: { slug: string; name: string; category: string; tags: string[]; seconds: number; job_id: string; stream_url: string }[]
    }
  },
  /** Kick off an SFX generation job */
  async generate(params: { text: string; seconds: number }) {
    const response = await api.post('/studio/sfx/generate', params)
    return response.data as { success?: boolean; job_id: string }
  },

  /** Poll SFX job status */
  async getStatus(jobId: string) {
    const response = await api.get(`/studio/sfx/status/${jobId}`)
    return response.data as {
      status: string
      progress?: number
      error_message?: string
      ready: boolean
      download_url?: string
    }
  },

  /** Same-origin download/stream URL for a finished SFX job */
  getDownloadUrl(jobId: string) {
    return buildApiUrl(`/studio/sfx/download/${jobId}`)
  },
}

// ── Image Generation API (qwen-image-t2i via legit2compute) ──
export const imageApi = {
  async generate(params: { prompt: string; width?: number; height?: number; negative?: string }) {
    const response = await api.post('/studio/image/generate', params)
    return response.data as { success?: boolean; job_id: string }
  },
  async getStatus(jobId: string) {
    const response = await api.get(`/studio/image/status/${jobId}`)
    return response.data as { status: string; progress?: number; error_message?: string; ready: boolean; download_url?: string }
  },
  getDownloadUrl(jobId: string) {
    return buildApiUrl(`/studio/image/download/${jobId}`)
  },
}

// ── Voice / Narration Generation API (legit2compute) ────────────
export const voiceApi = {
  /** Kick off a voiceover generation job */
  async generate(params: { text: string }) {
    const response = await api.post('/studio/voice/generate', params)
    return response.data as { success?: boolean; job_id: string }
  },

  /** Poll a voice job status */
  async getStatus(jobId: string) {
    const response = await api.get(`/studio/voice/status/${jobId}`)
    return response.data as {
      status: string
      progress?: number
      error_message?: string
      ready: boolean
      download_url?: string
    }
  },

  /** Same-origin download/stream URL for a finished voice job */
  getDownloadUrl(jobId: string) {
    return buildApiUrl(`/studio/voice/download/${jobId}`)
  },

  /** Whether the creator has set a cloned voice reference */
  async getVoiceProfile() {
    const response = await api.get('/studio/voice/profile')
    return response.data as { has_voice: boolean; label?: string; source?: string; updated_at?: string | null }
  },

  /** Set the cloned voice from an uploaded audio/video sample */
  async uploadVoiceSample(file: File) {
    const form = new FormData()
    form.append('audio', file)
    const response = await api.post('/studio/voice/profile/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })
    return response.data as { success: boolean; label: string; source: string }
  },

  /** Set the cloned voice by extracting audio from a video URL */
  async cloneVoiceFromVideo(params: { url?: string; video_id?: string; title?: string }) {
    const response = await api.post('/studio/voice/profile/from-video', params, { timeout: 240000 })
    return response.data as { success: boolean; label: string; source: string }
  },

  /** Remove the cloned voice reference */
  async deleteVoiceProfile() {
    const response = await api.delete('/studio/voice/profile')
    return response.data as { success: boolean }
  },
}

// ── Project History API ──────────────────────────────────────────
export const projectApi = {
  /** List user's saved projects */
  async listProjects(limit = 50, offset = 0) {
    const response = await api.get('/studio/projects', { params: { limit, offset } })
    return response.data as {
      success: boolean
      projects: Array<{
        id: string
        title: string
        video_url: string
        video_duration: number
        thumbnail_url?: string
        video_source?: string
        status: string
        segment_count: number
        export_count: number
        created_at: string
        updated_at: string
        last_opened_at: string
      }>
      total: number
    }
  },

  /** Get a single project with segments and exports */
  async getProject(projectId: string) {
    const response = await api.get(`/studio/projects/${projectId}`)
    return response.data as {
      success: boolean
      project: {
        id: string
        title: string
        video_url: string
        video_path: string
        video_duration: number
        thumbnail_url?: string
        video_source?: string
        metadata: Record<string, any>
        segments: Array<{
          id: string
          start_time: number
          end_time: number
          transcript: string
          title: string
          selected: boolean
          highlight_score?: number
          tags?: string[]
          padding_before: number
          padding_after: number
          order_index: number
        }>
        exports: Array<{
          export_id: string
          filename: string
          file_size?: number
          file_url?: string
          export_type: string
          destination_url?: string
          created_at: string
          expires_at?: string
        }>
      }
    }
  },

  /** Save/create a project (auto-save) */
  async saveProject(data: {
    id?: string
    title: string
    video_url: string
    duration: number
    thumbnail?: string
    platform?: string
    source_video_id?: string
    metadata?: Record<string, any>
    segments?: Array<Record<string, any>>
  }) {
    const response = await api.post('/studio/projects', data)
    return response.data as { success: boolean; project: { id: string } }
  },

  /** Update project (segments, selections) */
  async updateProject(projectId: string, data: {
    title?: string
    segments?: Array<Record<string, any>>
    selected_segments?: string[]
  }) {
    const response = await api.put(`/studio/projects/${projectId}`, data)
    return response.data as { success: boolean }
  },

  /** Delete a project */
  async deleteProject(projectId: string) {
    const response = await api.delete(`/studio/projects/${projectId}`)
    return response.data as { success: boolean }
  },

  /** Record an export */
  async recordExport(data: {
    project_id?: string
    filename: string
    file_size?: number
    file_url?: string
    export_type: string
    destination_url?: string
    clip_count?: number
    metadata?: Record<string, any>
  }) {
    const response = await api.post('/studio/exports', data)
    return response.data as { success: boolean; export: Record<string, any> }
  },

  /** List user's exports */
  async listExports() {
    const response = await api.get('/studio/exports')
    return response.data as {
      success: boolean
      exports: Array<{
        export_id: string
        project_id?: string
        project_title?: string
        thumbnail_url?: string
        filename: string
        file_size?: number
        file_url?: string
        export_type: string
        destination_url?: string
        created_at: string
        expires_at?: string
      }>
    }
  },

  /** Get storage usage */
  async getStorageUsage() {
    const response = await api.get('/studio/storage')
    return response.data as {
      success: boolean
      total_bytes: number
      project_count: number
      clip_count: number
      export_count: number
      storage_limit_bytes: number
      percent_used: number
    }
  },
}

// YouTube Publishing API
export const youtubeApi = {
  /** Check if user has connected YouTube channel */
  async getStatus() {
    const response = await api.get('/studio/youtube/status')
    return response.data as {
      configured: boolean
      connected: boolean
      channel_id?: string
      email?: string
    }
  },

  /** Get OAuth connect URL (redirect to this) */
  getConnectUrl() {
    return buildApiUrl('/studio/youtube/connect')
  },

  /** Disconnect YouTube channel */
  async disconnect() {
    const response = await api.post('/studio/youtube/disconnect')
    return response.data
  },

  /** Generate AI metadata for a clip */
  async generateMetadata(params: {
    clip_title: string
    original_title: string
    transcript_snippet?: string
    tags_hint?: string[]
  }) {
    const response = await api.post('/studio/youtube/generate-metadata', params)
    return response.data as {
      success: boolean
      title: string
      description: string
      tags: string[]
      category_id: string
    }
  },

  /** Upload clip to YouTube (multipart form) */
  async publish(params: {
    videoBlob: Blob
    title: string
    description: string
    tags: string[]
    privacy: 'private' | 'unlisted' | 'public'
    isShort: boolean
    notifySubscribers: boolean
  }, onProgress?: (pct: number) => void) {
    const formData = new FormData()
    formData.append('video', params.videoBlob, 'clip.mp4')
    formData.append('title', params.title)
    formData.append('description', params.description)
    formData.append('tags', params.tags.join(','))
    formData.append('privacy', params.privacy)
    formData.append('is_short', params.isShort ? 'true' : 'false')
    formData.append('notify_subscribers', params.notifySubscribers ? 'true' : 'false')

    const response = await api.post('/studio/youtube/publish', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,  // 10 min for large uploads
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      },
    })
    return response.data as {
      success: boolean
      video_id: string
      url: string
      title: string
      privacy: string
    }
  },

  /** List videos from user's connected YouTube channel */
  async listChannelVideos(params?: {
    maxResults?: number
    pageToken?: string
    query?: string
    order?: 'date' | 'viewCount' | 'title' | 'rating'
  }) {
    const response = await api.get('/studio/youtube/videos', {
      params: {
        max_results: params?.maxResults || 20,
        page_token: params?.pageToken || '',
        q: params?.query || '',
        order: params?.order || 'date',
      },
    })
    return response.data as {
      success: boolean
      videos: Array<{
        video_id: string
        title: string
        description: string
        thumbnail: string
        duration: number
        duration_formatted: string
        published_at: string
        view_count: number
        like_count: number
        comment_count: number
        url: string
      }>
      next_page_token: string | null
      total_results: number
    }
  },

  /** Generate AI-optimized metadata for YouTube Shorts */
  async generateSmartMetadata(params: {
    clip_title: string
    original_title: string
    transcript_text?: string
    clip_start?: number
    clip_end?: number
    tags_hint?: string[]
    use_style?: boolean
  }) {
    const response = await api.post('/studio/youtube/smart-metadata', params)
    return response.data as {
      success: boolean
      title: string
      description: string
      tags: string[]
      category_id: string
      method: 'llm' | 'template'
    }
  },

  /** Distill the creator's connected channel into a reusable Style Profile */
  async analyzeStyle() {
    const response = await api.post('/studio/youtube/analyze')
    return response.data as {
      success: boolean
      profile: Record<string, any>
      stats?: { videos_analyzed?: number; top_view_count?: number; median_duration_s?: number }
    }
  },

  /** Fetch the creator's stored Style Profile (or {profile:null}) */
  async getStyleProfile() {
    const response = await api.get('/studio/youtube/style-profile')
    return response.data as {
      profile: null | {
        summary?: string
        niche?: string[]
        content_pillars?: string[]
        voice_tone?: string
        title_patterns?: string[]
        hook_style?: string
        catchphrases?: string[]
        format?: string
        title_exemplars?: string[]
        do?: string[]
        dont?: string[]
      }
      stats?: { videos_analyzed?: number; top_view_count?: number; median_duration_s?: number }
      updated_at?: string | null
    }
  },
}

// Social Media API functions
export const socialMediaApi = {
  // Get user's connected social media accounts
  async getConnectedAccounts() {
    const response = await api.get('/social-media/accounts')
    return response.data
  },

  // Connect social media account
  async connectAccount(platform: string) {
    const response = await api.post(`/social-media/connect/${platform}`)
    return response.data
  },

  // Get intro/outro clips
  async getIntroOutroClips(type: 'intro' | 'outro') {
    const response = await api.get(`/social-media/clips/${type}`)
    return response.data
  },

  // Upload intro/outro clip
  async uploadClip(file: File, type: 'intro' | 'outro', name: string) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    formData.append('name', name)

    const response = await api.post('/social-media/upload-clip', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Delete intro/outro clip
  async deleteClip(clipId: string) {
    const response = await api.delete(`/social-media/clips/${clipId}`)
    return response.data
  },

  // Get hashtag suggestions
  async getHashtagSuggestions(query?: string) {
    const response = await api.get('/social-media/hashtags', {
      params: { query }
    })
    return response.data
  },

  // Publish to social media
  async publishContent(request: {
    video_id: string
    segment_ids: string[]
    intro_id?: string
    outro_id?: string
    platforms: string[]
    metadata: Record<string, {
      title: string
      description: string
      hashtags: string[]
      aspect_ratio?: string
    }>
    scheduled_at?: string
  }) {
    const response = await api.post('/social-media/publish', request)
    return response.data
  },

  // Get publishing status
  async getPublishingStatus(postId: string) {
    const response = await api.get(`/social-media/publish/${postId}/status`)
    return response.data
  },

  // Get publishing analytics
  async getAnalytics(postId: string) {
    const response = await api.get(`/social-media/publish/${postId}/analytics`)
    return response.data
  },

  // Get user's posts
  async getUserPosts(page = 1, limit = 20) {
    const response = await api.get('/social-media/posts', {
      params: { page, limit }
    })
    return response.data
  },

  // Cancel scheduled post
  async cancelScheduledPost(postId: string) {
    const response = await api.delete(`/social-media/publish/${postId}`)
    return response.data
  }
}

/**
 * The user's own sound library.
 *
 * Separate from `sfxApi`, which serves the curated set everyone shares. These are
 * the sounds the assistant means when someone says "my airhorn", so the slug is
 * the identity that matters — it is what the assistant emits and what the user
 * sees in the panel.
 */
export const libraryApi = {
  async list(): Promise<{ assets: any[]; reason?: string }> {
    const response = await api.get('/studio/library')
    return response.data
  },

  async upload(file: File, kind: 'sfx' | 'music' | 'voice' = 'sfx', opts?: { name?: string; tags?: string[] }) {
    const form = new FormData()
    form.append('file', file)
    form.append('kind', kind)
    if (opts?.name) form.append('name', opts.name)
    if (opts?.tags?.length) form.append('tags', opts.tags.join(','))
    // Longer than the default: this carries audio, not JSON, and a 50MB bed over a
    // domestic uplink will outlast a 30s timeout.
    const response = await api.post('/studio/library', form, { timeout: 300000 })
    return response.data
  },

  async remove(slug: string) {
    const response = await api.delete(`/studio/library/${encodeURIComponent(slug)}`)
    return response.data
  },

  /** Keep a generated sound. The bytes are COPIED into the user's library, because
   *  a job artifact is on a 7-day retention — a reference would resolve for a week
   *  and then quietly stop. */
  async saveFromJob(jobId: string, kind: 'sfx' | 'music' | 'voice', name: string) {
    const response = await api.post('/studio/library/from-job',
      { job_id: jobId, kind, name }, { timeout: 300000 })
    return response.data
  },

  /** Playable URL. Absolute via apiBaseURL so it also works in the desktop shell,
   *  where a relative path would resolve against the bundled asset scheme. */
  streamUrl(slug: string): string {
    return `${apiBaseURL}/studio/library/${encodeURIComponent(slug)}/audio`
  },
}
